// Users.cshtml (QOR staff): staff accounts of the chosen processor's programs; add one (they set their own password).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type UserSummary } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { useFilterForm, useQueryParams } from '../components/filters';
import { useStaff } from '../components/RequireStaff';
import { ChooseProcessor, FormError, StatusBadge } from '../components/ui';
import { STAFF_ROLE_OPTIONS } from '../scope';

const AVATARS = ['qc-avatar--accent', 'qc-avatar--success', 'qc-avatar--purple', 'qc-avatar--red'];

/** The search box: name, e-mail or role, case-insensitive. */
export function matchesUser(u: UserSummary, search: string | null): boolean {
  if (!search) return true;
  const s = search.toLowerCase();
  return [u.displayName, u.email, u.role].some((v) => v.toLowerCase().includes(s));
}

export function UsersPage() {
  usePageTitle('Users');
  const { scope } = useStaff();
  const client = useQueryClient();
  const search = useQueryParams().get('search');
  const submitSearch = useFilterForm('/users');
  const data = useQuery({
    queryKey: ['users', scope.activeProvider],
    enabled: !scope.needsProvider,
    queryFn: async () => {
      const [programs, users] = await Promise.all([adminApi.programs(), adminApi.users()]);
      return {
        programs: programs.filter((p) => p.provider === scope.activeProvider),
        users: users.filter((u) => u.provider === scope.activeProvider),
      };
    },
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const programOptions = data.data?.programs ?? [];
  const users = (data.data?.users ?? []).filter((u) => matchesUser(u, search));

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const programId = String(form.get('NewProgramId') ?? '');
    if (!programOptions.some((p) => p.id === programId))
      return setError(`You can only add users to a ${scope.activeProvider} program.`);
    setBusy(true);
    const r = await adminApi.createUser({
      email: String(form.get('NewEmail') ?? ''),
      displayName: String(form.get('NewDisplayName') ?? ''),
      programId,
      role: String(form.get('NewRole') ?? 'SupportUser'),
    });
    setBusy(false);
    if (!r.success) return setError(r.error ?? "Couldn't create this user.");
    setError(null);
    formEl.reset();
    await client.invalidateQueries({ queryKey: ['users'] });
  }

  return (
    <>
      <div className="qc-page-heading">
        <h1>Users</h1>
        <p className="qc-breadcrumb">
          QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Users</strong>
        </p>
      </div>

      {scope.needsProvider ? (
        <ChooseProcessor what="to see and manage its users" />
      ) : (
        <>
          <div className="qc-users-scope-row">
            <p className="qc-scope-label" style={{ margin: 0 }}>
              Showing <strong>{scope.activeProvider}</strong> users only
            </p>
            <div className="qc-users-scope-actions">
              <form className="qc-inline-search" onSubmit={submitSearch} key={search ?? ''}>
                <input
                  type="search"
                  name="search"
                  defaultValue={search ?? ''}
                  placeholder="Search by name, email, or role..."
                  aria-label="Search users"
                />
                <button type="submit" className="qc-button-ghost">
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="qc-panel" style={{ marginBottom: 20 }}>
            <div className="qc-panel-title">
              <h3>
                Add user{' '}
                <span className="qc-badge qc-badge-accent" style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                  Role Based Access
                </span>
              </h3>
              <span>
                Creates a staff account with a role -- they set their own password via &quot;Forgot password&quot; on
                sign in
              </span>
            </div>
            <FormError message={error} />
            <form
              onSubmit={(e) => void create(e)}
              className="qc-account-form"
              style={{ gridTemplateColumns: '2fr 2fr 1.5fr 1.5fr auto' }}
            >
              <label className="qc-account-field">
                <span>Email</span>
                <input type="email" name="NewEmail" placeholder="name@qorteams.com" required />
              </label>
              <label className="qc-account-field">
                <span>Name</span>
                <input type="text" name="NewDisplayName" required />
              </label>
              <label className="qc-account-field">
                <span>Program</span>
                <select name="NewProgramId">
                  {programOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="qc-account-field">
                <span>Role</span>
                <select name="NewRole" defaultValue="SupportUser">
                  {STAFF_ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="qc-account-refresh" disabled={busy}>
                + Add User
              </button>
            </form>
          </div>

          <div className="qc-panel">
            {data.isPending ? null : users.length === 0 ? (
              <p className="qc-empty">No users match this view.</p>
            ) : (
              <table className="qc-plain-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Program</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Password</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id}>
                      <td>
                        <div className="qc-user-row">
                          <span
                            className={`qc-user-avatar qc-user-avatar-sm ${AVATARS[i % AVATARS.length]}`}
                            aria-hidden="true"
                          >
                            {u.displayName.slice(0, 1)}
                          </span>
                          <strong>{u.displayName}</strong>
                        </div>
                      </td>
                      <td>{u.email}</td>
                      <td>{u.programName}</td>
                      <td>
                        <span className="qc-badge qc-badge-accent">{u.role}</span>
                      </td>
                      <td>
                        <StatusBadge status={u.status} />
                      </td>
                      <td>
                        <span className={`qc-badge ${u.hasPassword ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                          {u.hasPassword ? 'Created' : 'Not created'}
                        </span>
                      </td>
                      <td>
                        <Link to={`/users/${u.id}`}>
                          Manage <span aria-hidden="true">&rsaquo;</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}
